import Image from "next/image";
import Link from "next/link";
import styles from "./CompareCard.module.scss";
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import MileageModal from "@components/molecules/MileageModal";
import { CarVariant } from "@components/molecules/ComparisonVariantDetails";
import assetLoader from "@utils/internalImageLoder";
import ImageAtom from "@components/atoms/Image";
import { PAGE_TYPES } from "@constants/pageTypes";

interface CompareCardProps {
  make: string;
  model: string;
  image: string;
  variants: CarVariant[];
  selectedVariant?: CarVariant;
  onVariantChange: (variant: CarVariant) => void;
  onRemove: () => void;
  onEdit: () => void;
  onAddSlot: () => void;
  index: number; // Add index prop for positioning
  isSellable: boolean;
  isApp: boolean;
  cityName: string;
  slug?: string; // Add slug for navigation
  isEmpty?: boolean;
}

const CompareCard = ({
  make,
  model,
  image,
  variants,
  selectedVariant,
  onVariantChange,
  onRemove,
  onEdit,
  onAddSlot,
  index,
  isSellable,
  slug,
  isApp,
  cityName,
  isEmpty = false,
}: CompareCardProps) => {
  const [isOpenVariants, setIsOpenVariants] = useState<boolean>(false);
  const [showFloating, setShowFloating] = useState<boolean>(false);
  const [floatingPosition, setFloatingPosition] = useState({
    left: 0,
    width: 0,
  });

  const cardRef = useRef<HTMLDivElement>(null);
  const variantSelectRef = useRef<HTMLDivElement>(null);

  const handleOpenVariantsModal = () => {
    if (variants) {
      setIsOpenVariants(true);
    }
  };

  useEffect(() => {
    const currentCard = cardRef.current;

    if (!currentCard) return;

    // Calculate position based on the entire card, not just variant select
    const updatePosition = () => {
      const rect = currentCard.getBoundingClientRect();
      setFloatingPosition({
        left: rect.left,
        width: rect.width,
      });
    };

    // Create intersection observer
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const isVisible = entry.intersectionRatio > 0.3; // Show floating when less than 30% visible

          if (!isVisible) {
            updatePosition();
            setShowFloating(true);
          } else {
            setShowFloating(false);
          }
        });
      },
      {
        threshold: [0, 0.3, 0.7, 1],
        rootMargin: "-20px 0px", // Trigger a bit before it's completely out of view
      }
    );

    observer.observe(currentCard);

    // Update position on window resize
    const handleResize = () => {
      if (showFloating) {
        updatePosition();
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", handleResize);
    };
  }, [showFloating]);

  // Floating variant component
  const FloatingVariant = (isApp?: any) => {
    if (!showFloating) return null;

    return createPortal(
      <div
        className={`${styles.floatingVariant} ${isApp ? styles.floatingVariantApp : ''}`}
        style={{
          left: `${floatingPosition.left}px`,
          width: `${floatingPosition.width}px`,
        }}
        onClick={handleOpenVariantsModal}
      >
        <div className={styles.make}>
          {make} {model}
        </div>
        <div className={styles.variantSelect}>
          <div className={styles.text}>{selectedVariant?.variantName}</div>
          <ImageAtom
            className={styles.img}
            src={`${assetLoader()}/icons/Chevron_down_dark.svg`}
            alt="select-down"
            width={20}
            height={20}
          />
        </div>
      </div>,
      document.body
    );
  };

  return (
    <>
      {!isEmpty && (
        <MileageModal
          isOpen={!!isOpenVariants}
          onClose={() => setIsOpenVariants(false)}
          title={`Choose the ${model} variant`}
          variants={variants}
          onSelect={(variant) => {
            onVariantChange(variant as any);
            setIsOpenVariants(false);
          }}
          preventScoll={false}
          preventRedirection
          isSellable={isSellable}
          segmentPageType={PAGE_TYPES.COMPARISON_PAGE}
          segmentActionName="tap_btn_choose_a_variant"
          segmentSection="Comparison page"
          cityName={cityName}
          brandName={make}
          modelName={model}
          carAvailability={isSellable ? "Yes" : "No"}
        />
      )}

      <div ref={cardRef} className={`${styles.card} ${isEmpty ? styles.emptyCard : ""}`}>
        {isEmpty ? (
          <button className={styles.addSlotButton} onClick={onAddSlot}>
            Add / Select MMV
          </button>
        ) : (
          <>
            <div className={styles.cardHeader}>
              {!isApp && (
                <>
                  <button
                    className={`${styles.deleteBtn} ${styles.removeBtn}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      onRemove();
                    }}
                    aria-label={`Remove ${make} ${model}`}
                  >
                    <Image
                      src={`${assetLoader()}/delete.svg`}
                      alt="Delete"
                      width={14}
                      height={14}
                    />
                  </button>
                  <button className={styles.deleteBtn} onClick={onEdit} aria-label={`Edit ${make} ${model}`}>
                    <Image
                      src={`${assetLoader()}/icons/edit_dark.svg`}
                      alt="Edit"
                      width={14}
                      height={14}
                    />
                  </button>
                </>
              )}
            </div>
            {slug ? (
              <Link href={`/cars/${slug}`} className={styles.carLink}>
                <div className={styles.imageWrapper}>
                  <Image
                    src={image}
                    alt={make}
                    width={120}
                    height={60}
                    objectFit="contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = `${assetLoader()}/images/placeHolder-FallbackImage.png`;
                    }}
                  />
                </div>
                <div className={styles.make}>
                  {make} {model}
                </div>
              </Link>
            ) : (
              <>
                <div className={styles.imageWrapper}>
                  <Image
                    src={image}
                    alt={make}
                    width={120}
                    height={60}
                    objectFit="contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = `${assetLoader()}/images/placeHolder-FallbackImage.png`;
                    }}
                  />
                </div>
                <div className={styles.make}>
                  {make} {model}
                </div>
              </>
            )}

            <div
              ref={variantSelectRef}
              className={styles.variantSelect}
              onClick={handleOpenVariantsModal}
            >
              <div className={styles.text}>{selectedVariant?.variantName}</div>
              <ImageAtom
                className={styles.img}
                src={`${assetLoader()}/icons/Chevron_down_dark.svg`}
                alt="select-down"
                width={20}
                height={20}
              />
            </div>
          </>
        )}
      </div>

      {!isEmpty && <FloatingVariant isApp={isApp} />}
    </>
  );
};

export default CompareCard;
